import { batch } from "omi";
import { Router } from "@vaadin/router";
import map from "lodash-es/map";
import { v4 as uuid } from "uuid";

import { triggerToast } from "@/components/base/nw-toast";
import { FeatureSlice } from "@/store/features";
import { UserSlice } from "@/store/user";
import { NetworkSignal, networkSignal } from "@/utils/signals";
import { createStore } from "@/utils/store";
import { Portfolio, FeatureKey } from "@/api";
import { Store } from "@/store";

import { usePortfolioDocMutation } from "./mutations/usePortfolioDocMutation";
import { usePortfolioMutation } from "./mutations/usePortfolioMutation";
import { usePortfolio } from "./queries/usePortfolio";
import { PortfolioDocument } from "./models";

type State = {
  portfolioTitle: string;
  portfolioDescription: string;

  /**
   * The mode of the portfolio.
   * @default "view" - The user is viewing someone else's portfolio.
   * @default "edit" - The user is editing their own portfolio.
   */
  mode: "view" | "edit";

  /**
   * The nickname of the portfolio that the user is viewing.
   */
  viewing: string | undefined;
};

type NetworkState = {
  documents: NetworkSignal<PortfolioDocument[]>;
  uploadUrl: NetworkSignal<{ url: string; key: string } | undefined>;
};

type Actions = {
  isEditable: () => boolean;
  setViewingOptions: (nickname?: string) => void;
  stopViewingOtherPortfolio: () => void;
  isOwner: () => boolean;
  getOwnerInfo: () => { name: string; profileImg: string } | undefined;

  fetch: () => Promise<void>;
  getPortfolio: () => Portfolio | undefined;
  getShareLink: () => string | undefined;
  setPortfolioDetails: (details: { title?: string; description?: string }) => Promise<Portfolio | undefined>;

  hasReachedProLimit: () => boolean;

  getDocuments: () => PortfolioDocument[];
  getDocumentById: (id: string) => PortfolioDocument | undefined;
  addDocument: (document: PortfolioDocument) => Promise<PortfolioDocument | undefined>;
  deleteDocument: (item: PortfolioDocument) => Promise<void>;
  editDocument: (document: PortfolioDocument) => Promise<boolean>;
  generateUploadUrl: () => Promise<{ url: string; key: string } | undefined>;
};

type Slices = {
  userSlice: UserSlice;
  featureSlice: FeatureSlice;
  portfolioSlice: ReturnType<typeof usePortfolio>;
  updatePortfolioMutation: ReturnType<typeof usePortfolioMutation>;
  addDocumentMutation: ReturnType<typeof usePortfolioDocMutation>["add"];
  deleteDocumentMutation: ReturnType<typeof usePortfolioDocMutation>["delete"];
  editDocumentMutation: ReturnType<typeof usePortfolioDocMutation>["edit"];
};

function createPortfolioStore() {
  const userSlice = Store.user;
  const featureSlice = Store.features;

  return createStore<State, NetworkState, Actions, Slices>({
    initialState: {
      mode: "view",
      portfolioTitle: "",
      portfolioDescription: "",
      viewing: undefined,
    },
    networkState: {
      documents: networkSignal<PortfolioDocument[]>({
        default: [],
      }),
      uploadUrl: networkSignal<{ url: string; key: string } | undefined>({
        default: undefined,
      }),
    },
    slices: () => {
      const mutation = usePortfolioDocMutation();

      return {
        userSlice,
        featureSlice,
        portfolioSlice: usePortfolio(),
        updatePortfolioMutation: usePortfolioMutation(),
        addDocumentMutation: mutation.add,
        deleteDocumentMutation: mutation.delete,
        editDocumentMutation: mutation.edit,
      };
    },
    actions: ({ networkState, slices, store }) => {
      const {
        userSlice,
        featureSlice,
        portfolioSlice,
        addDocumentMutation,
        deleteDocumentMutation,
        updatePortfolioMutation,
        editDocumentMutation,
      } = slices;

      const isEditable = () => {
        return store.mode.value === "edit";
      };

      const isOwner = () => {
        if (!userSlice.isLoggedIn.value) {
          return false;
        }

        return userSlice.getNickname() === store.viewing.value || store.viewing.value == null;
      };

      const hasReachedProLimit = () => {
        const isPro = userSlice.isPro();

        if (isPro) return false;

        const documents = networkState.documents.signal.data.value ?? [];
        const limit = featureSlice.getFeatureLimit(FeatureKey.PORTFOLIO_DOCUMENT);

        return documents.length >= limit;
      };

      const setViewingOptions = (nickname?: string) => {
        const isLoggedIn = userSlice.isLoggedIn.value;
        const userNickname = userSlice.getNickname();

        if (isLoggedIn && userNickname === nickname) {
          // Logged in viewing your own public profile
          store.mode.value = "edit";
          store.viewing.value = nickname;
        } else if (isLoggedIn && nickname == null) {
          // Viewing own portfolio
          store.mode.value = "edit";
          store.viewing.value = userNickname;
        } else if (nickname) {
          // Viewing someone else's portfolio
          store.mode.value = "view";
          store.viewing.value = nickname;
        }
      };

      const fetchPortfolio = async () => {
        networkState.documents.signal.isLoading.value = true;
        await portfolioSlice.fetch(store.viewing.value);

        const portfolio = portfolioSlice.portfolio.data.value;
        if (portfolio) {
          batch(() => {
            store.portfolioTitle.value = portfolio.title;
            store.portfolioDescription.value = portfolio.description;
            networkState.documents.signal.data.value = portfolio.documents;
            networkState.documents.signal.isLoading.value = false;
          });
        }
      };

      return {
        isOwner,
        isEditable,
        setViewingOptions,
        hasReachedProLimit,
        fetch: fetchPortfolio,
        stopViewingOtherPortfolio: () => {
          setViewingOptions();
          Router.go("/portfolio");
        },
        getShareLink: () => {
          const portfolio = portfolioSlice.portfolio.data.value;
          if (!portfolio) return undefined;

          const nickname = store.viewing.value ?? userSlice.getNickname();
          if (!nickname) return undefined;

          return `${window.location.origin}/${nickname}/portfolio`;
        },
        getPortfolio: () => {
          const portfolio = portfolioSlice.portfolio.data.value;
          if (!portfolio) return { title: "", description: "", documents: [] };

          return {
            title: portfolio.title,
            description: portfolio.description,
            documents: networkState.documents.signal.data.value ?? [],
          };
        },
        getOwnerInfo: () => {
          const portfolio = portfolioSlice.portfolio.data.value;
          if (portfolio == null) return undefined;

          return {
            name: portfolio.ownerName!,
            profileImg: portfolio.ownerPicture!,
          };
        },
        setPortfolioDetails: async ({ title, description }: { title?: string; description?: string }) => {
          if (!isEditable() || !isOwner()) return undefined;

          const previousTitle = store.portfolioTitle.value;
          const previousDescription = store.portfolioDescription.value;

          if (title != null) {
            store.portfolioTitle.value = title;
          }

          if (description != null) {
            store.portfolioDescription.value = description;
          }

          const res = await updatePortfolioMutation.mutate({
            title: title ?? previousTitle,
            description: description ?? previousDescription,
          });

          if (res) {
            import("@/services/analyticsService").then(({ useAnalyticsService }) => {
              useAnalyticsService().trackPortfolioDetailsUpdated(
                title ?? previousTitle,
                description ?? previousDescription,
              );
            });
          } else {
            if (title != null) {
              store.portfolioTitle.value = previousTitle;
            }

            if (description != null) {
              store.portfolioDescription.value = previousDescription;
            }

            triggerToast({
              variant: "error",
              message: "Unable to save changes. Please try again",
            });
          }

          return res;
        },
        getDocuments: () => networkState.documents.signal.data.value ?? [],
        getDocumentById: (id: string) => {
          return networkState.documents.signal.data.value?.find(doc => doc.id === id);
        },
        addDocument: async (document: PortfolioDocument): Promise<PortfolioDocument | undefined> => {
          if (!isEditable() || hasReachedProLimit()) return undefined;

          const result = await addDocumentMutation.mutate(document);

          if (result) {
            const currentDocuments = networkState.documents.signal.data.value ?? [];
            networkState.documents.signal.data.value = [result, ...currentDocuments];
            networkState.documents.signal.data.update();

            if (result.id) {
              import("@/services/analyticsService").then(({ useAnalyticsService }) => {
                useAnalyticsService().trackPortfolioDocumentAdded(result.id!);
              });
            }

            return result;
          }

          return undefined;
        },
        deleteDocument: async (item: PortfolioDocument) => {
          if (!isEditable()) return;

          if (item.id) {
            await deleteDocumentMutation.mutate(item.id);

            let documents = networkState.documents.signal.data.value ?? [];
            documents = documents.filter(doc => doc.id !== item.id);
            networkState.documents.signal.data.value = documents;
            import("@/services/analyticsService").then(({ useAnalyticsService }) => {
              useAnalyticsService().trackPortfolioDocumentDeleted(item.id!);
            });
          }
        },
        editDocument: async (document: PortfolioDocument) => {
          if (!isEditable()) return false;

          if (document.id) {
            const result = await editDocumentMutation.mutate(document);

            if (result) {
              let currentDocuments = networkState.documents.signal.data.value ?? [];
              currentDocuments = map(currentDocuments, doc => {
                return doc.id === document.id ? document : doc;
              });

              networkState.documents.signal.data.value = currentDocuments;

              import("@/services/analyticsService").then(({ useAnalyticsService }) => {
                useAnalyticsService().trackPortfolioDocumentEdited(document.id!);
              });

              return true;
            }
          }
          return false;
        },
        generateUploadUrl: async () => {
          if (!isEditable()) return undefined;

          const uploadId = uuid();
          const fileKey = `portfolio_${uploadId}`;

          const asyncFunc = async () => {
            const res = await fetch(`/uploads/generate-signed-url?fileName=${encodeURIComponent(fileKey)}`);
            if (!res.ok || !res.url) {
              throw new Error("Failed to generate upload URL");
            }

            const resJson = await res.json();
            return { url: resJson.url, key: fileKey };
          };

          return await networkState.uploadUrl.run(asyncFunc);
        },
      };
    },
  });
}

export const PortfolioStore = createPortfolioStore();

export type PortfolioStore = typeof PortfolioStore;
